<script setup lang="ts">
import { computed, ref, watch } from "vue";
import KeyboardModal from "@/components/canvas/KeyboardModal.vue";
import { Facade, DraggerType } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { Logger } from "@/logger";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import TextToolField from "./options/TextToolField.vue";
import HandwritingToolButtons from "./HandwritingToolButtons.vue";
import TextToolDock from "./options/TextToolDock.vue";
import { useTextPanelCollapse } from "@/composable/useTextPanelCollapse";

/**
 * Contenu du panneau d'options de l'outil texte (ouvert à côté du bouton
 * dans la barre d'outils) : le texte à poser. Il est celui du PROCHAIN
 * placement — un texte déjà posé se modifie depuis la barre de sélection
 * (EditTextBtn).
 */

const { isCollapsed, expand } = useTextPanelCollapse();

const isModalVisible = ref(false);
const currentMessage = ref("");

function updateCurrentText(newMessage: string) {
  currentMessage.value = newMessage;
  DraggerMenuState.setActiveText(newMessage);
  // Also update the Facade text content
  if (Facade.isInitialized) {
    Facade.text.setContent(newMessage);
  }
}

function openModal() {
  Logger.info(`TextInputMenu.vue : openModal text is ${currentMessage.value}`);
  isModalVisible.value = true;
}

const isTextDraggerActive = computed(() => {
  return DraggerMenuState.activeDragger.name === DraggerType.Text;
});

// Watch for dragger changes via DraggerMenuState
watch(
  () => DraggerMenuState.activeDragger,
  () => {
    if (isTextDraggerActive.value) {
      DraggerMenuState.setActiveText(currentMessage.value);
      if (Facade.isInitialized) {
        Facade.text.setContent(currentMessage.value);
      }
    }
  },
);

// Un appui sur le champ ouvre la saisie ; pendant l'écriture manuscrite, il
// ramène d'abord au mode texte.
function onFieldOpen() {
  if (isTextDraggerActive.value) {
    openModal();
  } else {
    DraggerMenuState.setActiveDragger({ name: DraggerType.Text });
  }
}

const clear = () => {
  updateCurrentText("");
};

// Le menu d'outils ouvre l'éditeur quand on prend l'outil texte SANS texte
// en attente : on vient pour écrire, autant ne pas demander un deuxième
// appui. S'il y a déjà un texte, l'utilisateur vient sans doute le reposer.
function openEditorIfEmpty() {
  if (!currentMessage.value.length) {
    openModal();
  }
}

defineExpose({ openEditorIfEmpty });
</script>

<template>
  <div class="d-flex flex-column gap-2">
    <div>
      <!-- Ligne de titre : laisse la place a la croix du panneau, a droite -->
      <div class="text-tool-header text-tool-label">
        {{ $t("canvas:tools.text.tooltip.title") }}
      </div>
      <TextToolField
        :message="currentMessage"
        :active="isTextDraggerActive"
        @open="onFieldOpen"
        @clear="clear"
      />
    </div>

    <div class="text-tool-hint">
      <span class="text-tool-hint-icon">
        <PhosphorIcon name="pen-nib" :size="13" weight="bold" />
      </span>
      <span>{{ $t("canvas:tools.text.placeHint") }}</span>
    </div>

    <HandwritingToolButtons />

    <!-- Panneau réduit : le champ seul, en bas à gauche du canvas -->
    <TextToolDock
      v-if="isCollapsed"
      :message="currentMessage"
      @open="openModal"
      @clear="clear"
      @expand="expand"
    />

    <KeyboardModal
      v-model:visible="isModalVisible"
      v-model:message="currentMessage"
      @update="updateCurrentText"
      :multiline="true"
    />
  </div>
</template>

<!-- Styles du panneau (.text-tool-*) : globaux, dans app.scss -->
